import { useEffect, useState } from 'react';
import type { FieldDef, FieldOption, LookupDef } from '@jfh/intake-shared';
import { api } from '../../lib/api.js';
import { Icon } from '../../components/Icon.js';
import { MiniButton, SectionTitle, SegButtons, ToggleRow } from './ui.js';

const OTHER_TYPES = new Set(['select', 'radio', 'multiselect']);
const CUSTOM = '__custom__';

/** The answers as one list, whether stored as `options` or value/label `choices`. */
function answerLabels(f: FieldDef): string[] {
  return f.choices ? f.choices.map((c) => c.label || c.value) : (f.options ?? []);
}

/**
 * Answers to choose from (F9 P2): typed in here (reorder, remove, paste a list,
 * "let them type their own"), or a list from iQ picked by name, with a
 * preview. Value/label pairs ("Store a different value") live in More options.
 */
export function ChoicesSection({
  field: f,
  update,
  isAdmin,
}: {
  field: FieldDef;
  update: (patch: Partial<FieldDef>) => void;
  isAdmin: boolean;
}) {
  const iq = f.optionsSource?.type === 'iq';
  const labels = answerLabels(f);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Edit whichever list is stored, keeping each answer's stored value with it.
  const edit = (fn: <T>(arr: T[]) => T[]) =>
    f.choices ? update({ choices: fn([...f.choices]) }) : update({ options: fn([...(f.options ?? [])]) });
  const rename = (i: number, label: string) =>
    f.choices
      ? update({ choices: f.choices.map((c, j) => (j === i ? { ...c, label } : c)) })
      : update({ options: (f.options ?? []).map((o, j) => (j === i ? label : o)) });
  const move = (i: number, dir: -1 | 1) =>
    edit((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
      return arr;
    });
  const remove = (i: number) => edit((arr) => arr.filter((_, j) => j !== i));
  const add = () => {
    const label = `Answer ${labels.length + 1}`;
    if (f.choices) update({ choices: [...f.choices, { value: label, label }] });
    else update({ options: [...(f.options ?? []), label] });
  };
  const applyPaste = () => {
    const list = pasteText.split('\n').map((x) => x.trim()).filter(Boolean);
    if (f.choices) update({ choices: list.map((l) => ({ value: l, label: l })) });
    else update({ options: list });
    setPasteOpen(false);
  };

  const mark = f.type === 'radio' ? '○' : f.type === 'select' ? null : '□';

  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <SectionTitle>Answers to choose from</SectionTitle>
      <SegButtons
        options={[
          ['typed', 'Typed in here'],
          ['iq', 'A list from iQ'],
        ]}
        value={iq ? 'iq' : 'typed'}
        onChange={(v) =>
          update({ optionsSource: v === 'iq' ? { type: 'iq', key: f.optionsSource?.key ?? '' } : undefined })
        }
      />
      {iq ? (
        <IqListPicker
          value={f.optionsSource?.key ?? ''}
          onChange={(key) => update({ optionsSource: { type: 'iq', key } })}
          isAdmin={isAdmin}
        />
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {labels.length === 0 && (
            <div className="field-help" style={{ marginTop: 0 }}>
              No answers yet. Add some, or paste a list.
            </div>
          )}
          {labels.map((label, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ color: 'var(--text-3)', width: 16, display: 'flex', justifyContent: 'center', fontSize: 12 }}>
                {mark ?? i + 1}
              </span>
              <input
                className="input"
                style={{ height: 34 }}
                value={label}
                aria-label={`Answer ${i + 1}`}
                onChange={(e) => rename(i, e.target.value)}
              />
              <MiniButton label="Move answer up" disabled={i === 0} onClick={() => move(i, -1)}>
                ↑
              </MiniButton>
              <MiniButton label="Move answer down" disabled={i === labels.length - 1} onClick={() => move(i, 1)}>
                ↓
              </MiniButton>
              <MiniButton label="Remove answer" onClick={() => remove(i)}>
                <Icon name="x" size={13} />
              </MiniButton>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn sm" onClick={add}>
              <Icon name="plus" size={13} /> Add an answer
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setPasteText(labels.join('\n'));
                setPasteOpen((o) => !o);
              }}
            >
              Paste a list
            </button>
          </div>
          {pasteOpen && (
            <div>
              <textarea
                className="textarea"
                rows={5}
                placeholder="One answer per line"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button type="button" className="btn primary sm" onClick={applyPaste}>
                  Replace answers
                </button>
                <button type="button" className="btn ghost sm" onClick={() => setPasteOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {OTHER_TYPES.has(f.type) && (
            <div style={{ marginTop: 4 }}>
              <ToggleRow
                title="Let them type their own answer"
                hint="Adds “Other” with a box to type in."
                on={Boolean(f.allowOther)}
                onToggle={() => update({ allowOther: f.allowOther ? undefined : true })}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Pick an iQ list by its friendly name, with a preview of the answers. */
function IqListPicker({
  value,
  onChange,
  isAdmin,
}: {
  value: string;
  onChange: (key: string) => void;
  isAdmin: boolean;
}) {
  const [lists, setLists] = useState<LookupDef[] | null>(null);
  const [preview, setPreview] = useState<FieldOption[] | null>(null);
  const [custom, setCustom] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .listLookups()
      .then((r) => live && setLists(r.lookups))
      .catch(() => live && setLists([]));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setPreview(null);
    if (!value) return;
    let live = true;
    api
      .getLookup(value)
      .then((r) => live && setPreview(r.options))
      .catch(() => live && setPreview([]));
    return () => {
      live = false;
    };
  }, [value]);

  const known = lists ?? [];
  // A key that isn't a defined list (e.g. a path) keeps working as a custom key.
  const isCustom = custom || (lists !== null && value !== '' && !known.some((l) => l.key === value));

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <label className="field-label" style={{ margin: 0 }}>
        Which iQ list?
      </label>
      <select
        className="select"
        value={isCustom ? CUSTOM : value}
        onChange={(e) => {
          if (e.target.value === CUSTOM) setCustom(true);
          else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">Choose a list…</option>
        {known.map((l) => (
          <option key={l.key} value={l.key}>
            {l.name || l.key}
          </option>
        ))}
        <option value={CUSTOM}>Another list (by its key)…</option>
      </select>
      {isCustom && (
        <input
          className="input mono"
          value={value}
          placeholder="e.g. /salesrep"
          aria-label="List key"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {value && preview && (
        <div className="field-help" style={{ marginTop: 0 }}>
          {preview.length === 0
            ? 'No answers came back from this list yet.'
            : `Applicants will see ${preview.length} ${preview.length === 1 ? 'answer' : 'answers'}: ${preview
                .slice(0, 4)
                .map((o) => o.label)
                .join(', ')}${preview.length > 4 ? ' and more' : ''}.`}
        </div>
      )}
      <div className="field-help" style={{ marginTop: 0 }}>
        {isAdmin
          ? 'Lists are kept up to date from iQ. Add or change a list in Settings.'
          : 'Lists are kept up to date from iQ. Ask an admin if the list you need isn’t here.'}
      </div>
    </div>
  );
}
